import { registerPage } from '@zillow/client-profiler-interface-cs';
import { logError } from '@zg-rentals/log-error';
import { MIN_LG } from '@/src/lib/breakpoints';
import { readZgEnvironment } from '@/src/lib/zgEnv';

/**
 * What kind of page this is, declared by the route that renders it.
 *
 * Deliberately not derived from the URL: `/california` rewrites to
 * `/state/[stateResourceId]`, SRPs and HDPs share the `[resourceOrMaloneId]`
 * segment, and near-me SRPs live under their own prefix, so a path matcher
 * would encode routing trivia and drift as routes move.
 *
 * Types are grouped by what shares a rendering path, since that is what the
 * web-vitals gauges measure. `sitemap` covers the state and city hubs, the
 * only two pages built from SitemapSectionContent and seo/state-page-data,
 * and they were tuned as one surface. `user` covers every /user screen for
 * the same reason. Clickstream already separates all of these per screen, so
 * a bucket per route would only fragment the gauges.
 *
 * Two renderings, one list. Client Profiler and the Marlin web-vitals gauges
 * built on it keep the `hotpads-` prefix their dashboards already query;
 * the dataLayer gets the bare name.
 */
export const PAGE_TYPES = ['homepage', 'srp', 'hdp', 'bdp', 'sitemap', 'user'] as const;

export type PageType = (typeof PAGE_TYPES)[number];

export type ClientProfilerPageName = `hotpads-${PageType}`;

export type ClientProfilerPageContext = {
  pageName: ClientProfilerPageName | 'unknown';
  pageType?: PageType;
  platform: 'desktop' | 'mobile' | 'unknown';
};

const unknownClientProfilerPageContext: ClientProfilerPageContext = {
  pageName: 'unknown',
  platform: 'unknown',
};
let clientProfilerPageContext = unknownClientProfilerPageContext;
let landingClientProfilerPageContext = unknownClientProfilerPageContext;

export function getClientProfilerPageContext(): ClientProfilerPageContext {
  return clientProfilerPageContext;
}

/**
 * The bare page type for the route currently mounted, or undefined on routes
 * that have not adopted `ClientProfilerRegistrar` yet.
 *
 * Safe to read from an ancestor's effect: React flushes child effects first,
 * so the route has registered before `AppBootstrap` runs.
 */
export function getCurrentPageType(): PageType | undefined {
  return clientProfilerPageContext.pageType;
}

/**
 * The route that served this page load. CrUX attributes a page's whole Core Web Vitals
 * lifetime to the landing URL and does not treat a soft navigation as a new page view, so
 * this is the dimension to tag web-vitals metrics with. Unlike the live context it survives
 * a route change and its unmount cleanup. Stays `unknown` for a metric that finalises before
 * the first route registers.
 */
export function getLandingClientProfilerPageContext(): ClientProfilerPageContext {
  return landingClientProfilerPageContext;
}

export function registerClientProfilerPage(pageType: PageType) {
  if (typeof window === 'undefined') return;

  const pageName: ClientProfilerPageName = `hotpads-${pageType}`;
  const platform = window.matchMedia(MIN_LG).matches ? 'desktop' : 'mobile';
  const registration = { pageName, pageType, platform } as const;
  clientProfilerPageContext = registration;
  if (landingClientProfilerPageContext === unknownClientProfilerPageContext) {
    landingClientProfilerPageContext = registration;
  }

  try {
    registerPage({
      pageName,
      // Local dev renders an empty tag; hotpads-web reported 'localhost' there.
      environment: readZgEnvironment() || 'localhost',
      platform,
    });
  } catch (e) {
    logError({
      error: e,
      errorType: 'ClientProfilerRegistrationError',
      context: { pageName },
    });
  }

  return () => {
    if (clientProfilerPageContext === registration) {
      clientProfilerPageContext = unknownClientProfilerPageContext;
    }
  };
}
